/**
 * Умная фильтрация проценки: ползунки «Срок доставки» и «Одинаковых деталей» (CAB-630).
 *
 * Положение по умолчанию — для пользователя, который ещё ничего не трогал (в памяти
 * браузера пусто): срок — до 10 дней, одинаковых деталей — до 3. Это два обычных включённых
 * фильтра: они считаются в счётчике и сохраняются в пользовательский фильтр. Как только
 * пользователь сдвинул ползунок сам (в любое положение, включая «Все» / «Любой срок»), его
 * выбор запоминается, и дефолт к этому ползунку больше не применяется. Каждый ползунок
 * запоминается независимо. «Сбросить фильтры» снимает оба ограничения — крайнее правое
 * положение («Любой срок» / «Все»), и оно тоже запоминается: дефолт после сброса не
 * возвращается.
 *
 * Лимит повторов общий для одиночной проценки и Подбора и не привязан к профилю.
 * Срок одиночной проценки живёт между поисками на одной машине. Ручной Подбор
 * использует свой начальный срок с сервера и последний выбор по каждому подбору;
 * общую память срока не читает и не перезаписывает.
 *
 * Записываем только то, что меняет действующее значение: пока пользователь стоит на
 * дефолте, в памяти ничего нет — так позже можно поменять дефолт, не трогая тех, кто
 * выбрал положение сам.
 */

export const DEFAULT_DELIVERY_TERM_DAYS = 10;
export const DEFAULT_DETAIL_LIMIT = 3;

export const DELIVERY_TERM_MIN_DAYS = 1;
export const DELIVERY_TERM_MAX_DAYS = 40;
export const DELIVERY_TERM_ANY = DELIVERY_TERM_MAX_DAYS + 1;

/** Канонический срок ползунка; null — «Любой срок». */
export function resolveDeliveryTerm(value: unknown): number | null {
  return typeof value === "number" && isDeliveryTermInRange(value) ? value : null;
}

export const DETAIL_LIMIT_MIN = 3;
/** Крайнее правое положение ползунка «Одинаковых деталей» — «Все», лимит выключен. */
export const DETAIL_LIMIT_UNLIMITED = 21;

/** Контракт настроек умной фильтрации, который читает опрос результатов (стор проценки). */
export interface SmartFilterSettings {
  filterApply: boolean;
  maxTimeEnabled: boolean;
  maxTime: number;
  detailLimitEnabled: boolean;
  detailLimit: number;
}

export const SMART_FILTERS_STORAGE_KEY = "smartFiltersSettings";
export const DELIVERY_TERM_STORAGE_KEY = "eplexDeliveryTermFilter.v1";

/**
 * Явное «любой срок». Раньше выключенный срок просто удалялся из памяти, и его нельзя было
 * отличить от «пользователь ещё ничего не трогал» — а с появлением дефолта это разные состояния.
 */
const DELIVERY_TERM_ANY_STORAGE_VALUE = "any";

/**
 * Что хранится по ползунку: `undefined` — пользователь его не трогал (действует дефолт),
 * `null` — выбрал «Все» / «Любой срок», число — выбранная граница.
 */
type StoredSliderValue = number | null | undefined;

function readRaw(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    console.error("Smart filters save error: ", error);
  }
}

function isDeliveryTermInRange(value: number): boolean {
  return Number.isInteger(value) && value >= DELIVERY_TERM_MIN_DAYS && value <= DELIVERY_TERM_MAX_DAYS;
}

function isDetailLimitInRange(value: number): boolean {
  return Number.isInteger(value) && value >= DETAIL_LIMIT_MIN && value < DETAIL_LIMIT_UNLIMITED;
}

function parseStoredDeliveryTerm(raw: string | null): StoredSliderValue {
  if (raw === null) return undefined;
  if (raw === DELIVERY_TERM_ANY_STORAGE_VALUE) return null;
  const value = Number(raw);
  return isDeliveryTermInRange(value) ? value : undefined;
}

function parseStoredDetailLimit(raw: string | null): StoredSliderValue {
  if (raw === null) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<SmartFilterSettings> | null;
    if (!parsed || typeof parsed !== "object") return undefined;
    if (!parsed.detailLimitEnabled) return null;
    return typeof parsed.detailLimit === "number" && isDetailLimitInRange(parsed.detailLimit)
      ? parsed.detailLimit
      : null;
  } catch (error) {
    console.error("Smart filters load error: ", error);
    return undefined;
  }
}

/** Действующий срок доставки: сохранённый выбор пользователя или дефолт; `null` — любой срок. */
export function readDeliveryTerm(): number | null {
  const stored = parseStoredDeliveryTerm(readRaw(DELIVERY_TERM_STORAGE_KEY));
  return stored === undefined ? DEFAULT_DELIVERY_TERM_DAYS : stored;
}

/** Запоминает срок; `null` — «любой срок». Ничего не пишет, если действующее значение не меняется. */
export function writeDeliveryTerm(value: number | null): void {
  if (readDeliveryTerm() === value) return;
  writeRaw(DELIVERY_TERM_STORAGE_KEY, value === null ? DELIVERY_TERM_ANY_STORAGE_VALUE : String(value));
}

/** Действующий лимит одинаковых деталей: сохранённый выбор или дефолт; `null` — «Все». */
export function readDetailLimit(): number | null {
  const stored = parseStoredDetailLimit(readRaw(SMART_FILTERS_STORAGE_KEY));
  return stored === undefined ? DEFAULT_DETAIL_LIMIT : stored;
}

/** Запоминает лимит; `null` — «Все». Ничего не пишет, если действующее значение не меняется. */
export function writeDetailLimit(value: number | null): void {
  if (readDetailLimit() === value) return;
  writeRaw(SMART_FILTERS_STORAGE_KEY, JSON.stringify(buildSmartFilterSettings(value)));
}

/**
 * Настройки для запроса результатов по лимиту одинаковых деталей. Срок доставки в запрос не
 * уходит — он фильтруется на клиенте по `time_int`.
 */
export function buildSmartFilterSettings(detailLimit: number | null): SmartFilterSettings {
  const enabled = detailLimit !== null;
  return {
    filterApply: enabled,
    maxTimeEnabled: false,
    maxTime: 0,
    detailLimitEnabled: enabled,
    detailLimit: detailLimit ?? DETAIL_LIMIT_UNLIMITED,
  };
}

/** Действующие настройки для опроса результатов (с учётом дефолта). */
export function readSmartFilterSettings(): SmartFilterSettings {
  return buildSmartFilterSettings(readDetailLimit());
}

/**
 * Сколько ползунков ограничивают выдачу — столько отборов снимет «Сбросить фильтры».
 * Положение по умолчанию тоже ограничение (10 дн. / до 3), «Любой срок» / «Все» — нет.
 */
export function smartFiltersActiveCount(deliveryTerm: number | null, detailLimit: number | null): number {
  return (deliveryTerm === null ? 0 : 1) + (detailLimit === null ? 0 : 1);
}
